// src/app/components/chat/ChatComposer.tsx
//
// Caixa de envio da thread de chat interno. Preferência de transporte:
//   1. WebSocket (message.send) quando conectado e SEM anexo — a própria
//      mensagem chega de volta via o "eco" do grupo (chat.message.new),
//      então NÃO renderizamos otimisticamente aqui (evita ter que
//      reconciliar um id temporário com o id real quando o eco chegar).
//   2. REST (chatService.sendMessage) quando o socket não está conectado,
//      OU quando há qualquer anexo pendente — os arquivos já foram
//      enviados ao storage (POST /storage/upload/) e só precisam ser
//      vinculados à mensagem num único POST (attachment_ids). Nesse
//      caminho a mensagem é adicionada à thread via `onMessageSent` (não
//      passa pelo socket).
//
// Anexos: múltiplos arquivos por mensagem (imagem/vídeo/áudio/documento),
// preview local antes de enviar (thumbnail pra imagem/vídeo, player pra
// áudio), e nota de voz gravada na hora (MediaRecorder — mesmo padrão de
// VoiceRecorderButton.tsx, adaptado pra entrar como mais um anexo
// pendente em vez de enviar sozinha).
//
// Menção (@) é uma versão simples: digitar "@" abre um dropdown com os
// membros do grupo (busca lazy, uma vez); selecionar insere "@username "
// no texto e guarda o id em mentionedUserIds.

import { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, Paperclip, Send, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { storageService } from '../../services/storage';
import { chatService, type ChatMessage, type ChatParticipant } from '../../services/chatService';
import { ApiError } from '../../services/api';
import type { ChatSocket } from '../../lib/chatSocket';
import { categoryFromMime, fileIconFor, formatFileSize, getUserInitials } from './chatUtils';

export interface ChatComposerProps {
  groupId: string;
  socket: ChatSocket;
  socketConnected: boolean;
  onMessageSent: (message: ChatMessage) => void;
  placeholder?: string;
}

interface PendingAttachment {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  /** Object URL local (imagem/vídeo/áudio) — revogado ao remover/enviar. */
  previewUrl?: string;
}

const AUDIO_MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/ogg'];

function pickAudioMimeType(): string {
  if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
  return AUDIO_MIME_CANDIDATES.find((c) => MediaRecorder.isTypeSupported(c)) ?? '';
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

let typingThrottleAt = 0;

export default function ChatComposer({
  groupId,
  socket,
  socketConnected,
  onMessageSent,
  placeholder = 'Escreva uma mensagem... use @ para mencionar',
}: ChatComposerProps) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [mentionedUserIds, setMentionedUserIds] = useState<number[]>([]);

  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [members, setMembers] = useState<ChatParticipant[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ── Gravação de áudio ──
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    return () => {
      // Revoga todos os object URLs pendentes ao desmontar (troca de grupo).
      pendingAttachments.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const ensureMembersLoaded = async () => {
    if (members !== null) return;
    try {
      const list = await chatService.getGroupMembers(groupId);
      setMembers(list);
    } catch {
      setMembers([]);
    }
  };

  const handleTextChange = (value: string) => {
    setText(value);

    const cursor = textareaRef.current?.selectionStart ?? value.length;
    const upToCursor = value.slice(0, cursor);
    const atIndex = upToCursor.lastIndexOf('@');

    if (atIndex === -1 || /\s/.test(upToCursor.slice(atIndex + 1))) {
      setMentionQuery(null);
    } else {
      setMentionQuery(upToCursor.slice(atIndex + 1));
      void ensureMembersLoaded();
    }

    const now = Date.now();
    if (socketConnected && value.trim() && now - typingThrottleAt > 3000) {
      typingThrottleAt = now;
      socket.send({ type: 'typing' });
    }
  };

  const handlePickMention = (participant: ChatParticipant) => {
    const cursor = textareaRef.current?.selectionStart ?? text.length;
    const upToCursor = text.slice(0, cursor);
    const atIndex = upToCursor.lastIndexOf('@');
    if (atIndex === -1) return;

    const before = text.slice(0, atIndex);
    const after = text.slice(cursor);
    const nextText = `${before}@${participant.user.username} ${after}`;
    setText(nextText);
    setMentionedUserIds((prev) => (prev.includes(participant.user.id) ? prev : [...prev, participant.user.id]));
    setMentionQuery(null);

    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const filteredMembers = (members ?? []).filter((m) => {
    if (!mentionQuery) return true;
    const q = mentionQuery.toLowerCase();
    return m.user.username.toLowerCase().includes(q) || m.user.full_name.toLowerCase().includes(q);
  });

  const uploadFile = async (file: File) => {
    setUploadingCount((c) => c + 1);
    try {
      const uploaded = await storageService.upload(file, undefined, { chatGroupId: groupId });
      const category = categoryFromMime(file.type);
      const previewUrl = category === 'image' || category === 'video' || category === 'audio'
        ? URL.createObjectURL(file)
        : undefined;
      setPendingAttachments((prev) => [
        ...prev,
        { id: uploaded.id, name: uploaded.name, mimeType: file.type, size: file.size, previewUrl },
      ]);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : `Erro ao enviar "${file.name}".`;
      toast.error(msg);
    } finally {
      setUploadingCount((c) => c - 1);
    }
  };

  const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length === 0) return;
    await Promise.all(files.map(uploadFile));
  };

  const handleRemoveAttachment = (id: string) => {
    setPendingAttachments((prev) => {
      const target = prev.find((a) => a.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  };

  // ── Gravação de áudio ──

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleUploadRecording = async (mimeType: string) => {
    const blob = new Blob(chunksRef.current, { type: mimeType });
    chunksRef.current = [];
    const ext = mimeType.includes('ogg') ? 'ogg' : 'webm';
    const file = new File([blob], `audio-${Date.now()}.${ext}`, { type: mimeType || 'audio/webm' });
    await uploadFile(file);
  };

  const handleStartRecording = async () => {
    if (typeof MediaRecorder === 'undefined') {
      toast.error('Gravação de áudio não é suportada neste navegador.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickAudioMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      chunksRef.current = [];
      cancelledRef.current = false;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stopStream();
        if (cancelledRef.current) {
          chunksRef.current = [];
        } else {
          void handleUploadRecording(recorder.mimeType || mimeType || 'audio/webm');
        }
        setRecording(false);
        setElapsed(0);
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((v) => v + 1), 1000);
    } catch {
      toast.error('Não foi possível acessar o microfone — verifique a permissão do navegador.');
    }
  };

  const handleStopRecording = () => {
    stopTimer();
    mediaRecorderRef.current?.stop();
  };

  const handleCancelRecording = () => {
    stopTimer();
    cancelledRef.current = true;
    mediaRecorderRef.current?.stop();
  };

  // ── Envio ──

  const handleSend = async () => {
    const trimmed = text.trim();
    const uploading = uploadingCount > 0;
    if ((!trimmed && pendingAttachments.length === 0) || uploading) return;
    if (sending) return;

    setSending(true);
    try {
      if (pendingAttachments.length > 0) {
        // Anexo presente → sempre via REST (attachment_ids exige um POST síncrono).
        const message = await chatService.sendMessage(
          groupId,
          trimmed,
          mentionedUserIds.length ? mentionedUserIds : undefined,
          pendingAttachments.map((a) => a.id),
        );
        onMessageSent(message);
      } else if (socketConnected) {
        socket.send({
          type: 'message.send',
          content: trimmed,
          ...(mentionedUserIds.length ? { mentioned_user_ids: mentionedUserIds } : {}),
        });
      } else {
        const message = await chatService.sendMessage(groupId, trimmed, mentionedUserIds.length ? mentionedUserIds : undefined);
        onMessageSent(message);
      }

      pendingAttachments.forEach((a) => a.previewUrl && URL.revokeObjectURL(a.previewUrl));
      setText('');
      setMentionedUserIds([]);
      setPendingAttachments([]);
      setMentionQuery(null);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Erro ao enviar mensagem.';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const uploading = uploadingCount > 0;

  return (
    <div className="border-t border-slate-200 dark:border-white/[0.08] bg-white dark:bg-[#0a0b0d] p-3 relative">
      {mentionQuery !== null && filteredMembers.length > 0 && (
        <div className="absolute bottom-full left-3 mb-1 w-64 max-h-56 overflow-y-auto rounded-lg border border-slate-200 dark:border-white/[0.08] bg-white dark:bg-[#16181d] shadow-lg z-10">
          {filteredMembers.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => handlePickMention(m)}
              className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50 dark:hover:bg-white/[0.06] text-left"
            >
              <div className="size-6 rounded-full bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                {getUserInitials(m.user)}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-slate-700 dark:text-slate-200 truncate">{m.user.full_name}</p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">@{m.user.username}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      {pendingAttachments.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {pendingAttachments.map((att) => {
            const category = categoryFromMime(att.mimeType);
            return (
              <div
                key={att.id}
                className="group relative flex items-center gap-2 rounded-lg border border-slate-200 dark:border-white/[0.08] bg-slate-50 dark:bg-white/[0.03] p-1.5 pr-2"
              >
                {category === 'image' && att.previewUrl ? (
                  <img src={att.previewUrl} alt={att.name} className="size-10 rounded object-cover flex-shrink-0" />
                ) : category === 'video' && att.previewUrl ? (
                  <video src={att.previewUrl} className="size-10 rounded object-cover flex-shrink-0" muted />
                ) : category === 'audio' && att.previewUrl ? (
                  <audio src={att.previewUrl} controls className="h-8 w-40" />
                ) : (
                  (() => {
                    const { Icon, className } = fileIconFor(att.mimeType, att.name);
                    return <Icon className={`size-6 flex-shrink-0 ${className}`} />;
                  })()
                )}
                {category !== 'audio' && (
                  <div className="min-w-0 max-w-[140px]">
                    <p className="text-xs text-slate-600 dark:text-slate-400 truncate">{att.name}</p>
                    <p className="text-[10px] text-slate-400 dark:text-slate-500">{formatFileSize(att.size)}</p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveAttachment(att.id)}
                  className="absolute -top-1.5 -right-1.5 size-4 rounded-full bg-slate-700 dark:bg-slate-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remover"
                >
                  <X className="size-3" />
                </button>
              </div>
            );
          })}
          {uploading && (
            <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-white/[0.08] bg-slate-50 dark:bg-white/[0.03] px-3 py-1.5">
              <Loader2 className="size-3.5 animate-spin text-slate-400 dark:text-slate-500" />
              <span className="text-xs text-slate-500 dark:text-slate-400">Enviando…</span>
            </div>
          )}
        </div>
      )}

      <div className="flex items-end gap-2">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFilesSelected}
          disabled={sending}
        />

        {recording ? (
          <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 flex-shrink-0">
            <span className="relative flex size-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full size-2 bg-red-500" />
            </span>
            <span className="text-xs font-mono text-red-700 dark:text-red-300 tabular-nums w-9">{formatElapsed(elapsed)}</span>
            <button
              type="button"
              onClick={handleCancelRecording}
              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-500/20 text-red-500 dark:text-red-400"
              title="Cancelar gravação"
            >
              <Trash2 className="size-3.5" />
            </button>
            <button
              type="button"
              onClick={handleStopRecording}
              className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-500/20 text-red-600 dark:text-red-400"
              title="Adicionar áudio"
            >
              <Send className="size-3.5" />
            </button>
          </div>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="flex-shrink-0"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              title="Anexar arquivo (imagem, vídeo, áudio, documento...)"
            >
              <Paperclip className="size-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="flex-shrink-0"
              onClick={() => void handleStartRecording()}
              disabled={sending}
              title="Gravar áudio"
            >
              <Mic className="size-4" />
            </Button>
          </>
        )}

        <Textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => handleTextChange(e.target.value)}
          placeholder={placeholder}
          rows={1}
          className="flex-1 min-h-[40px] max-h-32 text-sm"
          disabled={sending}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void handleSend();
            }
            if (e.key === 'Escape') setMentionQuery(null);
          }}
        />

        <Button
          type="button"
          size="icon"
          className="flex-shrink-0 bg-indigo-600 hover:bg-indigo-700"
          onClick={() => void handleSend()}
          disabled={sending || uploading || (!text.trim() && pendingAttachments.length === 0)}
          title="Enviar"
        >
          {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />}
        </Button>
      </div>
    </div>
  );
}
